import { useEffect, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { audioBus } from "../audioBus";
import type { FlightTape } from "../flightTape";

/**
 * Plays a flight tape: drives the camera along the recorded track (position
 * lerp + quaternion slerp between 30 Hz samples) and feeds the recorded audio
 * features into audioBus — so every audio-reactive effect performs the same
 * show it did during the flight, re-rendered live. The AudioSampler stands
 * down via audioBus.replayFeed while this is active.
 */
export function ReplayRig({
  tape, active, loop, onEnd, sticksOut,
}: {
  tape: FlightTape | null;
  active: boolean;
  loop: boolean;
  onEnd: () => void;
  sticksOut: React.MutableRefObject<[number, number, number, number]>;
}) {
  const { camera } = useThree();
  const t = useRef(0);
  const loopRef = useRef(loop); loopRef.current = loop;
  const tmp = useRef({ qa: new THREE.Quaternion(), qb: new THREE.Quaternion() });

  useEffect(() => {
    if (!active || !tape) return;
    const cam = camera as THREE.PerspectiveCamera;
    const prevFov = cam.fov;
    t.current = 0;
    cam.fov = 100 + tape.meta.fisheye * 40; // same lens the flight was flown with
    cam.updateProjectionMatrix();
    audioBus.replayFeed = true;
    return () => {
      audioBus.replayFeed = false;
      cam.fov = prevFov;
      cam.position.set(0, 1.5, 12);
      cam.lookAt(0, 0, 0);
      cam.updateProjectionMatrix();
    };
  }, [active, tape, camera]);

  useFrame((_, dtRaw) => {
    if (!active || !tape) return;
    const dt = Math.min(dtRaw, 1 / 30);
    t.current += dt;
    const dur = tape.n / tape.hz;
    if (t.current >= dur) {
      if (loopRef.current) t.current %= dur;
      else { onEnd(); return; }
    }

    const ft = Math.min(t.current * tape.hz, tape.n - 1);
    const i0 = Math.floor(ft);
    const i1 = Math.min(i0 + 1, tape.n - 1);
    const a = ft - i0;
    const cam = camera as THREE.PerspectiveCamera;
    const P = tape.pos, Q = tape.quat, S = tape.sticks, A = tape.audio;

    cam.position.set(
      P[i0 * 3] + (P[i1 * 3] - P[i0 * 3]) * a,
      P[i0 * 3 + 1] + (P[i1 * 3 + 1] - P[i0 * 3 + 1]) * a,
      P[i0 * 3 + 2] + (P[i1 * 3 + 2] - P[i0 * 3 + 2]) * a
    );
    const T = tmp.current;
    T.qa.set(Q[i0 * 4], Q[i0 * 4 + 1], Q[i0 * 4 + 2], Q[i0 * 4 + 3]);
    T.qb.set(Q[i1 * 4], Q[i1 * 4 + 1], Q[i1 * 4 + 2], Q[i1 * 4 + 3]);
    T.qa.slerp(T.qb, a);
    cam.quaternion.copy(T.qa);

    const v = audioBus.values;
    v.level = A[i0 * 5] + (A[i1 * 5] - A[i0 * 5]) * a;
    v.bass = A[i0 * 5 + 1] + (A[i1 * 5 + 1] - A[i0 * 5 + 1]) * a;
    v.mid = A[i0 * 5 + 2] + (A[i1 * 5 + 2] - A[i0 * 5 + 2]) * a;
    v.treble = A[i0 * 5 + 3] + (A[i1 * 5 + 3] - A[i0 * 5 + 3]) * a;
    v.beat = A[i0 * 5 + 4] + (A[i1 * 5 + 4] - A[i0 * 5 + 4]) * a;

    const so = sticksOut.current;
    for (let k = 0; k < 4; k++) so[k] = S[i0 * 4 + k] + (S[i1 * 4 + k] - S[i0 * 4 + k]) * a;
  });

  return null;
}
